import React, { useState } from "react";
import PropTypes from "prop-types";
import { Button } from "rsuite";
import TrendingUpIcon from "../../../../assets/img/action-center/trending-up.svg";
import ChevronRightWhiteIcon from "../../../../assets/img/action-center/chevron-right-white.svg";
import TickSmIcon from "../../../../assets/img/action-center/tick-sm.svg";
import CloseSmIcon from "../../../../assets/img/action-center/close-sm.svg";
import { getClientLogo } from "../../../../utils/clientLogos";
import CorridorModal from "../corridor-modal/CorridorModal";
import useHeapCardViewed from "../../../../hooks/useHeapCardViewed";
import "./CorridorCard.scss";

// Color palette for client icons
const CLIENT_COLORS = [
  "#3B82F6", // Blue
  "#F97316", // Orange
  "#8B5CF6", // Purple
  "#10B981", // Green
  "#EC4899", // Pink
  "#14B8A6", // Teal
];

const CorridorCard = ({
  data = [],
  onDismiss,
  propertyId,
  cardPosition,
  hotelName,
}) => {
  const cardRef = useHeapCardViewed("corridor", cardPosition, hotelName);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [submissionState, setSubmissionState] = useState("idle"); // 'idle' | 'submitted'
  const [submittedClients, setSubmittedClients] = useState([]);
  const [rejectedClients, setRejectedClients] = useState([]);

  const handleOpenNow = () => {
    setIsModalOpen(true);
  };

  const handleMaybeLater = () => {
    if (onDismiss) {
      onDismiss();
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleModalSuccess = (selectedIds) => {
    setSubmittedClients(selectedIds);
    const rejected = data
      .filter((client) => !selectedIds.includes(client.id))
      .map((client) => client.id);
    setRejectedClients(rejected);
    setSubmissionState("submitted");
    setIsModalOpen(false);
  };

  const getClientStatus = (clientId) => {
    if (submissionState !== "submitted") return null;
    if (submittedClients.includes(clientId)) return "check";
    if (rejectedClients.includes(clientId)) return "x";
    return null;
  };

  const isSubmitted = submissionState === "submitted";

  return (
    <div
      ref={cardRef}
      className={`corridor-card${isSubmitted ? " corridor-card--submitted" : ""}`}
    >
      <div className="corridor-card-badge">
        <img src={TrendingUpIcon} alt="" className="badge-icon" />
        <span>Revenue Opportunity</span>
      </div>

      <h3 className="corridor-card-title">
        Unlock demand in high-value markets
      </h3>

      {isSubmitted ? (
        <p className="corridor-card-description">
          Your request has been sent. We&apos;ll notify you once the selected
          buyers have been activated.
        </p>
      ) : (
        <p className="corridor-card-description">
          Open corridors to these top travel buyers currently closed to your
          property.
        </p>
      )}

      <ul className="corridor-card-clients">
        {data.slice(0, 5).map((client, index) => {
          const status = getClientStatus(client.id);
          return (
            <li key={client.id} className="corridor-card-client">
              <div className="client-icon-wrapper">
                {getClientLogo(client.clientName) ? (
                  <img
                    src={getClientLogo(client.clientName)}
                    alt={client.clientName}
                    className="client-icon"
                  />
                ) : (
                  <span
                    data-testid={`client-fallback-${client.id}`}
                    className="client-icon client-icon-fallback"
                    style={{ backgroundColor: CLIENT_COLORS[index % 6] }}
                  />
                )}
              </div>
              <span className="client-name">{client.clientName}</span>
              {status === "check" && (
                <span
                  className="client-status-check"
                  data-testid={`client-status-${client.id}`}
                >
                  <img src={TickSmIcon} alt="" />
                </span>
              )}
              {status === "x" && (
                <span
                  className="client-status-x"
                  data-testid={`client-status-${client.id}`}
                >
                  <img src={CloseSmIcon} alt="" />
                </span>
              )}
              {!status && client.bookingPerMonth != null && (
                <span className="client-metric">
                  +{client.bookingPerMonth} bookings/mth
                </span>
              )}
            </li>
          );
        })}
      </ul>

      <div className="corridor-card-actions">
        {isSubmitted ? (
          <div className="request-sent-pill">
            <span>Request Sent</span>
          </div>
        ) : (
          <>
            <div className="corridor-card-actions-left">
              <Button
                id="action-corridor-later"
                appearance="link"
                className="maybe-later-btn"
                onClick={handleMaybeLater}
              >
                Maybe Later
              </Button>
            </div>
            <Button
              id="action-corridor-open"
              appearance="primary"
              className="open-now-btn"
              onClick={handleOpenNow}
            >
              Open Now
              <img src={ChevronRightWhiteIcon} alt="" className="btn-chevron" />
            </Button>
          </>
        )}
      </div>

      {isModalOpen && (
        <CorridorModal
          data={data}
          propertyId={propertyId}
          onClose={handleModalClose}
          onSuccess={handleModalSuccess}
        />
      )}
    </div>
  );
};

CorridorCard.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      propertyId: PropTypes.number,
      clientId: PropTypes.number,
      clientName: PropTypes.string.isRequired,
      market: PropTypes.string,
      clientMarketRank: PropTypes.number,
      isConnected: PropTypes.bool,
      clientGmvLast30Days: PropTypes.number,
      clientGmvLast90Days: PropTypes.number,
      clientBookingVolumeLast30Days: PropTypes.number,
      clientBookingVolumeLast90Days: PropTypes.number,
      bookingPerMonth: PropTypes.number,
      runDate: PropTypes.string,
    })
  ).isRequired,
  onDismiss: PropTypes.func,
  propertyId: PropTypes.number,
  cardPosition: PropTypes.number,
  hotelName: PropTypes.string,
};

export default CorridorCard;
